// SPDX-License-Identifier: AGPL-3.0-or-later
import {AgentsConnectionCard, agentsConnectionVisible} from '@app/features/konvo_agents/components/AgentsConnection';
import AgentSettingsPage from '@app/features/konvo_agents/state/AgentSettingsPage';
import {AssistantConnectorCards} from './AssistantConnectorCards';
// KONVO: the Microsoft 365 and ClickUp connector cards, wired to their status.
// KonvoConnectionsGrid is the settings view; KonvoConnectPrompt is the single
// card the calendar / mail / tasks pages show when nothing is linked yet.

import {openKonvoClaudeConnectModal} from '@app/features/konvo_connections/components/KonvoClaudeConnectModal';
import {KonvoClickUpManager} from '@app/features/konvo_connections/components/KonvoClickUpManager';
import {openKonvoCodexConnectModal} from '@app/features/konvo_connections/components/KonvoCodexConnectModal';
import {KonvoConnectorCard} from '@app/features/konvo_connections/components/KonvoConnectorCard';
import styles from '@app/features/konvo_connections/components/KonvoConnectorCard.module.css';
import {
	KonvoTeamsGroupsCard,
	KonvoTeamsGroupsManager,
} from '@app/features/konvo_connections/components/KonvoTeamsGroupsCard';
import {KonvoWhatsAppCard} from '@app/features/konvo_connections/components/KonvoWhatsAppCard';
import KonvoConnections from '@app/features/konvo_connections/state/KonvoConnections';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {ClaudeLogo} from '@app/features/ui/components/icons/ClaudeLogo';
import {ClickUpLogo} from '@app/features/ui/components/icons/ClickUpLogo';
import {MicrosoftLogo} from '@app/features/ui/components/icons/MicrosoftLogo';
import {OpenAILogo} from '@app/features/ui/components/icons/OpenAILogo';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import {useEffect, useState} from 'react';

const MICROSOFT_NAME_DESCRIPTOR = msg({message: 'Microsoft 365', comment: 'Connector card title.'});
const MICROSOFT_DESCRIPTION_DESCRIPTOR = msg({
	message: 'Your Outlook calendar, recent mail and Teams meeting links, inside Konvo.',
	comment: 'Connector card description for Microsoft 365.',
});
const MICROSOFT_CONNECT_DESCRIPTOR = msg({message: 'Connect Microsoft', comment: 'Connector card button.'});
const MICROSOFT_RECONNECT_DESCRIPTOR = msg({
	message: 'Reconnect Microsoft',
	comment: 'Connector card button shown when Konvo needs fresh permissions.',
});
const CLICKUP_NAME_DESCRIPTOR = msg({message: 'ClickUp', comment: 'Connector card title.'});
const CLICKUP_DESCRIPTION_DESCRIPTOR = msg({
	message: 'Create and complete tasks from any message, under your own ClickUp account.',
	comment: 'Connector card description for ClickUp.',
});
const CLICKUP_CONNECT_DESCRIPTOR = msg({message: 'Connect ClickUp', comment: 'Connector card button.'});
const CLICKUP_MANAGE_DESCRIPTOR = msg({
	message: 'Manage',
	comment: 'ClickUp connector card button: opens the ClickUp manager (statuses that count as done in Konvo).',
});
const CLICKUP_UNAVAILABLE_DESCRIPTOR = msg({
	message: "ClickUp sign-in isn't configured on this instance yet.",
	comment: 'Shown instead of the connect button when the instance has no ClickUp OAuth client.',
});
const CLAUDE_NAME_DESCRIPTOR = msg({message: 'Claude', comment: 'Connector card title.'});
const CLAUDE_DESCRIPTION_DESCRIPTOR = msg({
	message: 'Your own Claude subscription powering the Konvo Assistant: it knows your day, your tasks and your mail.',
	comment: 'Connector card description for Claude.',
});
const CLAUDE_CONNECT_DESCRIPTOR = msg({message: 'Connect Claude', comment: 'Connector card button.'});
const CLAUDE_RECONNECT_DESCRIPTOR = msg({
	message: 'Reconnect Claude',
	comment: 'Connector card button shown when the stored Claude token stopped working.',
});
const CLAUDE_UNAVAILABLE_DESCRIPTOR = msg({
	message: "The Claude assistant isn't enabled on this instance yet.",
	comment: 'Shown instead of the connect button when the instance has no Claude runtime.',
});
const CLAUDE_ON_DESCRIPTOR = msg({message: 'Assistant on', comment: 'Connector card account line for Claude.'});
const CLAUDE_PAUSED_DESCRIPTOR = msg({message: 'Assistant paused', comment: 'Connector card account line for Claude.'});
const FEATURE_ASSISTANT_DESCRIPTOR = msg({message: 'Assistant DM', comment: 'Connector card feature chip.'});
const FEATURE_SUMMARIES_DESCRIPTOR = msg({message: 'Summaries', comment: 'Connector card feature chip.'});
const FEATURE_MEMORY_DESCRIPTOR = msg({message: 'Memory', comment: 'Connector card feature chip.'});
const DISCONNECT_DESCRIPTOR = msg({message: 'Disconnect', comment: 'Connector card button.'});
const FEATURE_CALENDAR_DESCRIPTOR = msg({message: 'Calendar', comment: 'Connector card feature chip.'});
const FEATURE_MAIL_DESCRIPTOR = msg({message: 'Mail', comment: 'Connector card feature chip.'});
const FEATURE_TEAMS_DESCRIPTOR = msg({message: 'Teams links', comment: 'Connector card feature chip.'});
const FEATURE_FREE_DESCRIPTOR = msg({message: '/free', comment: 'Connector card feature chip for the /free command.'});
const FEATURE_TRANSCRIPTS_DESCRIPTOR = msg({
	message: 'Meeting transcripts',
	comment: 'Connector card feature chip shown once Teams transcripts are allowed.',
});
const ALLOW_TRANSCRIPTS_DESCRIPTOR = msg({
	message: 'Allow meeting transcripts',
	comment: 'Microsoft 365 card button that asks Microsoft for Teams transcript access (needs admin approval).',
});
const FEATURE_TASKS_DESCRIPTOR = msg({message: 'Tasks', comment: 'Connector card feature chip.'});
const FEATURE_TASK_COMMAND_DESCRIPTOR = msg({
	message: '/task',
	comment: 'Connector card feature chip for the /task command.',
});
const FEATURE_REMINDERS_DESCRIPTOR = msg({message: 'Reminders', comment: 'Connector card feature chip.'});

function useConnectionStatus(): void {
	useEffect(() => {
		void KonvoConnections.refresh();
		const onFocus = () => void KonvoConnections.refresh();
		window.addEventListener('focus', onFocus);
		return () => window.removeEventListener('focus', onFocus);
	}, []);
}

export const KonvoMicrosoftCard = observer(
	({needsConsent = false, solo = false}: {needsConsent?: boolean; solo?: boolean}) => {
		const {i18n} = useLingui();
		const connected = KonvoConnections.microsoftConnected === true && !needsConsent;
		return (
			<KonvoConnectorCard
				logo={<MicrosoftLogo size={24} data-flx="konvo-connections.microsoft-card.microsoft-logo" />}
				name={i18n._(MICROSOFT_NAME_DESCRIPTOR)}
				description={i18n._(MICROSOFT_DESCRIPTION_DESCRIPTOR)}
				features={[
					i18n._(FEATURE_CALENDAR_DESCRIPTOR),
					i18n._(FEATURE_MAIL_DESCRIPTOR),
					i18n._(FEATURE_TEAMS_DESCRIPTOR),
					i18n._(FEATURE_FREE_DESCRIPTOR),
					...(KonvoConnections.microsoftTranscripts ? [i18n._(FEATURE_TRANSCRIPTS_DESCRIPTOR)] : []),
				]}
				connected={connected}
				actionLabel={i18n._(
					connected
						? DISCONNECT_DESCRIPTOR
						: needsConsent
							? MICROSOFT_RECONNECT_DESCRIPTOR
							: MICROSOFT_CONNECT_DESCRIPTOR,
				)}
				actionVariant={connected ? 'secondary' : 'primary'}
				onAction={() =>
					connected ? void KonvoConnections.disconnectMicrosoft() : void KonvoConnections.connectMicrosoft()
				}
				secondaryActionLabel={
					connected && !KonvoConnections.microsoftTranscripts ? i18n._(ALLOW_TRANSCRIPTS_DESCRIPTOR) : undefined
				}
				onSecondaryAction={
					connected && !KonvoConnections.microsoftTranscripts
						? () => void KonvoConnections.connectMicrosoft(true)
						: undefined
				}
				busy={KonvoConnections.busy === 'microsoft'}
				solo={solo}
				data-flx="konvo-connections.microsoft-card.konvo-connector-card"
			/>
		);
	},
);

export const KonvoClickUpCard = observer(({solo = false, onManage}: {solo?: boolean; onManage?: () => void}) => {
	const {i18n} = useLingui();
	const status = KonvoConnections.clickup;
	const connected = status?.connected === true;
	// Connected in Settings: Manage leads, Disconnect sits beside it (like the Agents card).
	const manage = connected && onManage != null;
	return (
		<KonvoConnectorCard
			logo={<ClickUpLogo size={24} data-flx="konvo-connections.clickup-card.click-up-logo" />}
			name={i18n._(CLICKUP_NAME_DESCRIPTOR)}
			description={i18n._(CLICKUP_DESCRIPTION_DESCRIPTOR)}
			features={[
				i18n._(FEATURE_TASKS_DESCRIPTOR),
				i18n._(FEATURE_TASK_COMMAND_DESCRIPTOR),
				i18n._(FEATURE_REMINDERS_DESCRIPTOR),
			]}
			connected={connected}
			account={status?.clickup_email ?? null}
			actionLabel={i18n._(
				manage ? CLICKUP_MANAGE_DESCRIPTOR : connected ? DISCONNECT_DESCRIPTOR : CLICKUP_CONNECT_DESCRIPTOR,
			)}
			actionVariant={connected ? 'secondary' : 'primary'}
			onAction={() =>
				manage
					? onManage()
					: connected
						? void KonvoConnections.disconnectClickUp()
						: void KonvoConnections.connectClickUp()
			}
			secondaryActionLabel={manage ? i18n._(DISCONNECT_DESCRIPTOR) : undefined}
			onSecondaryAction={manage ? () => void KonvoConnections.disconnectClickUp() : undefined}
			busy={KonvoConnections.busy === 'clickup'}
			note={status && !status.oauth_available ? i18n._(CLICKUP_UNAVAILABLE_DESCRIPTOR) : undefined}
			solo={solo}
			data-flx="konvo-connections.clickup-card.konvo-connector-card"
		/>
	);
});

/** Claude: the user's own subscription powering the Assistant DM. Connect opens the guided flow. */
export const KonvoClaudeCard = observer(({solo = false}: {solo?: boolean}) => {
	const {i18n} = useLingui();
	const status = KonvoConnections.claude;
	const connected = status?.connected === true;
	const needsReconnect = connected && status?.status === 'auth_error';
	return (
		<KonvoConnectorCard
			logo={<ClaudeLogo size={24} data-flx="konvo-connections.claude-card.claude-logo" />}
			name={i18n._(CLAUDE_NAME_DESCRIPTOR)}
			description={i18n._(CLAUDE_DESCRIPTION_DESCRIPTOR)}
			features={[
				i18n._(FEATURE_ASSISTANT_DESCRIPTOR),
				i18n._(FEATURE_SUMMARIES_DESCRIPTOR),
				i18n._(FEATURE_MEMORY_DESCRIPTOR),
			]}
			connected={connected && !needsReconnect}
			account={connected ? (status?.enabled ? i18n._(CLAUDE_ON_DESCRIPTOR) : i18n._(CLAUDE_PAUSED_DESCRIPTOR)) : null}
			actionLabel={i18n._(
				needsReconnect ? CLAUDE_RECONNECT_DESCRIPTOR : connected ? DISCONNECT_DESCRIPTOR : CLAUDE_CONNECT_DESCRIPTOR,
			)}
			actionVariant={connected && !needsReconnect ? 'secondary' : 'primary'}
			onAction={() =>
				connected && !needsReconnect ? void KonvoConnections.disconnectClaude() : openKonvoClaudeConnectModal()
			}
			secondaryActionLabel={needsReconnect ? i18n._(DISCONNECT_DESCRIPTOR) : undefined}
			onSecondaryAction={needsReconnect ? () => void KonvoConnections.disconnectClaude() : undefined}
			busy={KonvoConnections.busy === 'claude'}
			note={status && !status.available ? i18n._(CLAUDE_UNAVAILABLE_DESCRIPTOR) : undefined}
			solo={solo}
			data-flx="konvo-connections.claude-card.konvo-connector-card"
		/>
	);
});

export const KonvoCodexCard = observer(({solo = false}: {solo?: boolean}) => {
	const {i18n} = useLingui();
	const status = KonvoConnections.codex;
	const reconnect = status?.connected && status.status === 'auth_error';
	const disconnect = () => {
		void KonvoConnections.disconnectCodex().catch(() =>
			ToastCommands.error(i18n._(msg`Could not disconnect Codex. Please try again.`)),
		);
	};
	return (
		<KonvoConnectorCard
			logo={<OpenAILogo size={24} />}
			name="Codex"
			description={i18n._(msg`Your ChatGPT account powering the Konvo Assistant.`)}
			features={[
				i18n._(FEATURE_ASSISTANT_DESCRIPTOR),
				i18n._(FEATURE_SUMMARIES_DESCRIPTOR),
				i18n._(FEATURE_MEMORY_DESCRIPTOR),
			]}
			connected={status?.connected === true && !reconnect}
			account={status?.email}
			actionLabel={
				reconnect
					? i18n._(msg`Reconnect Codex`)
					: status?.connected
						? i18n._(DISCONNECT_DESCRIPTOR)
						: i18n._(msg`Connect Codex`)
			}
			actionVariant={status?.connected && !reconnect ? 'secondary' : 'primary'}
			onAction={() => {
				if (status?.connected && !reconnect) disconnect();
				else if (status?.available) openKonvoCodexConnectModal();
			}}
			secondaryActionLabel={reconnect ? i18n._(DISCONNECT_DESCRIPTOR) : undefined}
			onSecondaryAction={reconnect ? disconnect : undefined}
			busy={KonvoConnections.busy === 'codex' || (!status?.available && !status?.connected)}
			note={!status?.available ? i18n._(msg`Codex is not available on this server.`) : undefined}
			solo={solo}
		/>
	);
});

/** All connectors side by side — used in user settings. */
export const KonvoConnectionsGrid = observer(() => {
	useConnectionStatus();
	const [managingTeams, setManagingTeams] = useState(false);
	const [managingClickUp, setManagingClickUp] = useState(false);
	// KONVO: Agents (per-person switch) opens its own pages, which take over the tab (LinkedAccountsTab).
	const showAgents = agentsConnectionVisible();
	if (managingTeams && KonvoConnections.microsoftConnected && KonvoConnections.microsoftGrantCurrent !== false) {
		return <KonvoTeamsGroupsManager onBack={() => setManagingTeams(false)} />;
	}
	if (managingClickUp && KonvoConnections.clickup?.connected) {
		return <KonvoClickUpManager onBack={() => setManagingClickUp(false)} />;
	}
	return (
		<div className={styles.grid} data-flx="konvo-connections.grid">
			<KonvoMicrosoftCard
				needsConsent={KonvoConnections.microsoftConnected === true && KonvoConnections.microsoftGrantCurrent === false}
				data-flx="konvo-connections.grid.konvo-microsoft-card"
			/>
			{KonvoConnections.microsoftConnected === true && KonvoConnections.microsoftGrantCurrent !== false && (
				<KonvoTeamsGroupsCard onManage={() => setManagingTeams(true)} />
			)}
			<KonvoClickUpCard
				onManage={() => setManagingClickUp(true)}
				data-flx="konvo-connections.grid.konvo-click-up-card"
			/>
			{(KonvoConnections.codex?.available || KonvoConnections.codex?.connected) && <KonvoCodexCard />}
			{KonvoConnections.claude?.available && <KonvoClaudeCard data-flx="konvo-connections.grid.konvo-claude-card" />}
			{showAgents && <AgentsConnectionCard onManage={() => AgentSettingsPage.open()} />}
			<KonvoWhatsAppCard />
			<AssistantConnectorCards />
		</div>
	);
});

/**
 * Only the connectors that still need linking; renders nothing once everything is
 * connected. Used at the top of the Today page, whose cards are empty without them.
 */
export const KonvoMissingConnections = observer(() => {
	useConnectionStatus();
	const microsoftMissing = KonvoConnections.microsoftConnected === false;
	const clickupMissing = KonvoConnections.clickup?.oauth_available === true && !KonvoConnections.clickup.connected;
	const claudeMissing = KonvoConnections.claudeOffered;
	const codexMissing =
		KonvoConnections.codex?.available && !KonvoConnections.codex.connected && !KonvoConnections.claude?.connected;
	if (!microsoftMissing && !clickupMissing && !claudeMissing && !codexMissing) return null;
	return (
		<div className={styles.grid} data-flx="konvo-connections.missing">
			{microsoftMissing && <KonvoMicrosoftCard data-flx="konvo-connections.missing.konvo-microsoft-card" />}
			{clickupMissing && <KonvoClickUpCard data-flx="konvo-connections.missing.konvo-click-up-card" />}
			{codexMissing && <KonvoCodexCard />}
			{claudeMissing && <KonvoClaudeCard data-flx="konvo-connections.missing.konvo-claude-card" />}
		</div>
	);
});

/** One connector, centred — used by the calendar, mail and tasks empty states. */
export const KonvoConnectPrompt = observer(
	({
		connector,
		title,
		needsConsent = false,
	}: {
		connector: 'microsoft' | 'clickup';
		title: string;
		needsConsent?: boolean;
	}) => {
		useConnectionStatus();
		return (
			<div className={styles.prompt} data-flx="konvo-connections.connect-prompt">
				<h2 className={styles.promptTitle} data-flx="konvo-connections.connect-prompt.title">
					{title}
				</h2>
				{connector === 'microsoft' ? (
					<KonvoMicrosoftCard
						needsConsent={needsConsent}
						solo
						data-flx="konvo-connections.connect-prompt.konvo-microsoft-card"
					/>
				) : (
					<KonvoClickUpCard solo data-flx="konvo-connections.connect-prompt.konvo-click-up-card" />
				)}
			</div>
		);
	},
);
